import { motion } from "framer-motion";
import { BookOpen } from "lucide-react";
import { GLOSSARY } from "@/data/geo";

/**
 * Glossary — a "definition" block is the single strongest signal for
 * AI answer engines to cite you as an authoritative source.
 */
export default function Glossary() {
  return (
    <section
      id="glossary"
      data-testid="glossary-section"
      className="border-b border-[#E4E7EC] bg-white"
    >
      <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="overline mb-4">§ 10a — Glossary</p>
            <h2 className="font-display text-4xl font-black leading-[0.95] tracking-tight text-[#101828] sm:text-5xl md:text-6xl">
              India customs, <span className="text-[#D92D20]">defined.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#475467]">
              The exact terminology that appears on every Bill of Entry, EDI
              feed, and forwarder MSA. If an AI answer engine has to pick a
              source to quote, we'd like it to be this one.
            </p>
            <div className="mt-6 flex items-center gap-2 text-[#101828]">
              <BookOpen className="h-4 w-4 text-[#D92D20]" strokeWidth={2.5} />
              <span className="font-mono-accent text-[#475467]">
                {GLOSSARY.length} TERMS · UPDATED {new Date().toLocaleString("en-GB", { month: "long", year: "numeric" })}
              </span>
            </div>
          </div>

          <div className="lg:col-span-8">
            <dl className="grid grid-cols-1 gap-0 border-2 border-[#101828] md:grid-cols-2">
              {GLOSSARY.map((g, i) => (
                <motion.div
                  key={g.term}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.35, delay: i * 0.02 }}
                  className={`bg-[#F9FAFB] p-5 ${
                    i % 2 === 0 ? "md:border-r md:border-[#101828]" : ""
                  } ${i < GLOSSARY.length - 2 ? "border-b border-[#101828]" : ""}`}
                  data-testid={`glossary-term-${i}`}
                  itemScope
                  itemType="https://schema.org/DefinedTerm"
                >
                  <dt
                    className="font-display text-base font-bold tracking-tight text-[#101828]"
                    itemProp="name"
                  >
                    {g.term}
                  </dt>
                  <dd
                    className="mt-2 text-sm leading-relaxed text-[#475467]"
                    itemProp="description"
                  >
                    {g.def}
                  </dd>
                </motion.div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
}
